import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MaterialDetailsViewModel } from "api/factories/creatives.factory";
import { TabTypes } from "pages/MaterialSpecs/components";
import { usePermissions } from "hooks";
import { useSelector } from "react-redux";
import { getIsInternalUser } from "store/features/authorization";

export interface TabHeader {
    value: TabTypes;
    label: string;
    tooltip: string;
    disabled: boolean;
    display: boolean;
}

export const useTabHeaders = (
    materialDetails: MaterialDetailsViewModel | null,
    isOptionalSelected: boolean,
    isMaterialEmpty: boolean,
    isPrinted: boolean
) => {
    const { t } = useTranslation();
    const isInternalUser = useSelector(getIsInternalUser);
    const { INFO, ANNOTATIONS, ASSETS } = usePermissions(materialDetails?.permissions);
    // show only for optional, empty digitals, and digitals that were created by third party tag. Never show for prints
    const showThirdPartyTab =
        !isPrinted &&
        (materialDetails?.isThirdPartyTagMaterial ||
            materialDetails?.inputFileName === "" ||
            isOptionalSelected ||
            isMaterialEmpty);
    // this is done to avoid MatUI tab warnings,
    // when <TabList /> root elements are different than <Tab /> (even null or <></>), MatUI will generate warnings
    return useMemo<TabHeader[]>(() => {
        const showOnlyBasicTabs = isOptionalSelected && showThirdPartyTab;
        const isMaterialOptionalOrEmpty = isOptionalSelected || isMaterialEmpty;

        return [
            {
                label: "",
                value: "WELCOME_TAB",
                display: !isInternalUser,
                disabled: false,
                tooltip: "",
            },
            {
                label: t("campaign.label.customerSummary"),
                value: "CUSTOMER_SUMMARY",
                display: !isOptionalSelected,
                disabled: false,
                tooltip: "",
            },
            {
                label: t("campaign.label.details"),
                value: "DETAILS",
                display: showOnlyBasicTabs ? !showOnlyBasicTabs : !!INFO?.display,
                disabled: isMaterialOptionalOrEmpty || !!INFO?.disabled,
                tooltip: INFO?.tooltip || "",
            },
            {
                label: t("campaign.label.validation"),
                value: "VALIDATION",
                display: !showOnlyBasicTabs,
                disabled: isMaterialOptionalOrEmpty,
                tooltip: "",
            },
            {
                label: t("campaign.thirdPartyTag.label.thirdParty"),
                value: "THIRD_PARTY_TAG",
                display: showThirdPartyTab,
                disabled: false,
                tooltip: "",
            },
            {
                label: t("campaign.label.assets"),
                value: "ASSETS",
                display: showOnlyBasicTabs ? !showOnlyBasicTabs : !!ASSETS?.display,
                disabled: !!ASSETS?.disabled,
                tooltip: ASSETS?.tooltip || "",
            },
            {
                label: t("campaign.label.annotations"),
                value: "ANNOTATIONS",
                display: showOnlyBasicTabs ? !showOnlyBasicTabs : !!ANNOTATIONS?.display,
                disabled: isMaterialOptionalOrEmpty || !!ANNOTATIONS?.disabled,
                tooltip: ANNOTATIONS?.tooltip || "",
            },
            {
                label: t("campaign.label.versions"),
                value: "VERSIONS",
                display: isPrinted && !isMaterialOptionalOrEmpty,
                disabled: false,
                tooltip: "",
            },
            {
                label: t("campaign.label.history"),
                value: "HISTORY",
                display: !showOnlyBasicTabs,
                disabled: false,
                tooltip: "",
            },
        ];
    }, [
        isPrinted,
        isOptionalSelected,
        isMaterialEmpty,
        showThirdPartyTab,
        t,
        ANNOTATIONS,
        ASSETS,
        INFO,
        isInternalUser,
    ]);
};
